import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Lists.module.css';
import { ListCommand } from '../../../types/ListCommands';
import LinkedListView from'../listengine/LinkedListEngine';

const LINKED_LIST_CODE = `class Node {
  constructor(value) {
    this.value = value;
    this.next = null;
  }
}

class LinkedList {
  constructor() {
    this.head = null;
  }

  insertAt(value, index) { }
  deleteAt(index) { }
}`;

const MAX_LIST_SIZE = 10;

const LinkedListVisualizer: React.FC = () => {
  const navigate = useNavigate();
  const [command, setCommand] = useState<ListCommand | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputIndex, setInputIndex] = useState('');
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [list, setList] = useState<{ value: number }[]>([
    { value: 12 }, { value: 99 }, { value: 37 }, { value: 59 }, { value: 22 }
  ]);

  const handleInsert = () => {
    if (isAnimating || inputValue === '' || inputIndex === '') return;
    setIsAnimating(true);
    setActiveLine(11); 
    setCommand({
      id: Date.now(),
      action: 'insertAt',
      value: parseInt(inputValue),
      index: parseInt(inputIndex),
    });
    setInputValue('');
    setInputIndex('');
  };
  
  const handleDelete = () => {
    if (isAnimating || inputIndex === '') return;
    setIsAnimating(true);
    setActiveLine(12);
    setCommand({ id: Date.now(), action: 'removeAt', index: parseInt(inputIndex) });
    setInputIndex('');
  };
  
  const handleGenerateRandom = () => {
    const randomValue = Math.floor(Math.random() * 100);
    const randomIndex = list.length > 0 ? Math.floor(Math.random() * list.length) : 0;
    setInputValue(randomValue.toString());
    setInputIndex(randomIndex.toString());
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    setActiveLine(null); 
  };
  const handleReset = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCommand({ id: Date.now(), action: 'reset' });
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>      
      <h1 className={styles.title}>Linked List</h1>
      <div className={styles.description}>
          <h2>Description</h2>
         <p>A Linked List is a chain of nodes. Each node contains a value and a pointer to the next node in the chain. Unlike an array, nodes are not stored in contiguous memory, allowing for efficient insertions and deletions.</p>
         </div>
         <div className={styles.useCase}>
           <h2>Use Cases</h2>
           <p>Used to implement other data structures like stacks and queues. Also used for dynamic memory allocation, and in applications where frequent insertions/deletions are needed, like a photo viewer's next/previous navigation.</p>
         </div>
         <div className={styles.timeComplexity}>
           <h2>Time Complexity</h2>
           <p>Insertion/Deletion at a known position is fast at <strong>O(1)</strong>, but finding that position first (Access/Search) takes <strong>O(n)</strong> time because we must traverse the list from the beginning.</p>
         </div>
      <div className={styles.mainContent}>
        <div style={{flex: 1}}>
          <LinkedListView command={command} onComplete={handleAnimationComplete} />
        </div>
        <div className={styles.controlsAndCode}>
            <div className={styles.controls}>
                <input type="number" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Value" disabled={isAnimating} />
                <input type="number" value={inputIndex} onChange={e => setInputIndex(e.target.value)} placeholder="Index" disabled={isAnimating} />
                <button onClick={handleInsert} disabled={isAnimating}>Insert At</button>
                <button onClick={handleDelete} disabled={isAnimating}>Delete At</button>
                <button onClick={handleGenerateRandom} disabled={isAnimating}>Generate</button>
                <button onClick={handleReset} disabled={isAnimating}>Reset</button>
            </div>
            <div className={styles.codeDisplay}>
                {LINKED_LIST_CODE.split('\n').map((line, index) => (
                  <div key={index} className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}>
                    {line || ' '}
                  </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedListVisualizer;